export type RoadmapPhase = {
  name: string;
  summary: string;
  milestones: string[];
};

/**
 * Index into `phases` for the currently-active phase. Single source of truth for both
 * Progress.tsx (home teaser) and Roadmap.tsx (full page) — update this by hand as the
 * platform moves through phases, kept in sync with the internal ROADMAP.md by a human
 * (see README.md "Before going live"). This is a rough guide, not a live status feed.
 */
export const ACTIVE_PHASE_INDEX = 1;

export const phases: RoadmapPhase[] = [
  {
    name: 'Foundation',
    summary:
      'The multi-tenant core: authentication, tenant isolation, the data model every module builds on, and the security posture (encrypted secrets, outbound-only monitoring, audited access) the rest of the platform inherits.',
    milestones: [
      'Multi-tenant architecture with per-tenant data isolation',
      'Encrypted secrets storage — integration credentials never stored in plaintext',
      'Outbound-only agent architecture for on-prem monitoring — no inbound firewall exposure',
      'Core auth, roles, and audit logging across every write action',
    ],
  },
  {
    name: 'Core modules',
    summary:
      'Helpdesk, endpoint management (UEM) & RMM, Microsoft 365 backup, the security suite, CRM & sales, unified tenant management, compliance, and the client/staff portal — the modules that make Nexus a full PSA + RMM + backup replacement, not a point tool.',
    milestones: [
      'Helpdesk & ticketing with email-to-ticket intake and SLA tracking',
      'Endpoint management & RMM: native outbound-only agent, signed remote jobs, patch deployment, remote control',
      'Microsoft 365 backup to your own S3-compatible bucket, verified by hash',
      'Security suite: credential vault, phishing simulation, breach monitoring, vulnerability scanning',
      'CRM & sales pipeline, with won-deal conversion into onboarding and tenant provisioning',
      'Unified tenant management: HQ console, hard per-tenant isolation, secure switch-into-tenant support',
      'Compliance posture tracking across NIST CSF, SOC 2, HIPAA, PCI-DSS, ISO 27001, CIS, and FERPA/COPPA',
      'Client and staff portals with electronic agreement signing',
      'The agentic AI layer — AI-drafted diagnosis and resolution across every module, human-approved today',
    ],
  },
  {
    name: 'Private beta',
    summary:
      'A small set of design-partner MSPs and organizations run Nexus against real workloads. This is where the agentic AI capabilities graduate from human-approved to autonomous one class at a time, and where the first honest comparison and case-study content gets written.',
    milestones: [
      'Design partners onboarded onto the full module set',
      'Autonomous resolution of routine, non-destructive ticket classes (printer offline, password reset, troubleshooting) graduates from human-approved to hands-off, one class at a time — destructive and irreversible actions always escalate to a human',
      'Inbound & outbound AI calling goes live — including AI-initiated escalation and approval calls',
      'Feedback loop directly into the build — private beta shapes the GA feature set, not the other way around',
      'First real comparison and case-study content, written from what actually shipped',
    ],
  },
  {
    name: 'General availability',
    summary:
      'Open signup, full documentation, and a support model built for scale — the platform ready to onboard any MSP or organization, not just design partners.',
    milestones: [
      'Open self-serve signup and onboarding',
      'Full public documentation and integration guides',
      'SLA-backed support tiers',
      'Continued module and integration expansion driven by customer demand',
    ],
  },
];
